import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'
import { getApiBaseUrl } from '../lib/apiBaseUrl'
import { getThemeById } from '../config/themes'
import './HtdAuth.css'
import {
  closeFirebaseAuthBridge,
  primeFirebaseAuthBridgeWindow,
  redirectToFirebaseAuthBridgeSend,
  redirectToFirebaseAuthBridgeVerify,
  reportFirebaseSmsBilling,
  saveLineBridgeFlowContext,
  sendOTPViaFirebaseAuthBridge,
  shouldUseFirebaseAuthBridge,
  shouldUseFirebaseAuthBridgeRedirect,
  verifyOTPViaFirebaseAuthBridge,
  clearLineBridgeFlowContext,
  loadLineBridgeFlowContext,
  buildHashRouterReturnUrl,
} from '../lib/firebaseAuthBridge'
import {
  persistModernRegisterCompleteFlowState,
  type ModernRegisterCompleteFlowState,
} from '@huay/shared/registerFlowState'
import {
  verifyRegistrationOTP,
  checkMobileAvailability,
  checkRegistrationOTPCooldown,
  recordRegistrationOTPSent,
  getSMSConfigPublic,
  normalizeThaiMobile,
  isValidThaiMobile,
  toE164Thai,
} from '../services/registrationService'
import { resolveReferralToken } from '../services/affiliateService'

/** @deprecated use reportFirebaseSmsBilling from firebaseAuthBridge */
function reportFirebaseSMSUsage(tenantId: string, e164Phone: string) {
  reportFirebaseSmsBilling({
    apiBaseUrl: getApiBaseUrl(),
    tenantId,
    phoneNumber: e164Phone,
    status: 'sent',
    messageType: 'registration',
    source: 'frontend-modern-registration',
  })
}
declare global {
  interface Window {
    phoneConfirmation?: { confirm: (code: string) => Promise<{ user: { uid: string } }> }
    recaptchaVerifier?: { clear?: () => void } | null
  }
}

/** หน้า Register (สมัครสมาชิก) — ส่ง OTP ผ่าน backend หรือ Firebase Client (เมื่อ tenant ใช้ Firebase) */
interface ModernRegisterPageProps {
  themeConfig: TenantThemeConfig | null
}

type PersistedOtpCooldown = {
  phoneNumber: string
  cooldownUntil: number
}

type PersistedOtpFlow = {
  phone: string
  otpRequested: boolean
  sessionId: string | null
  cooldownUntil: number | null
}

const AFFILIATE_REFERRAL_KEY = 'affiliate_referral_code'

const toThaiPhoneDisplay = (phoneNumber?: string) => {
  if (!phoneNumber) return ''
  const cleaned = String(phoneNumber).trim()
  if (cleaned.startsWith('+66') && cleaned.length >= 12) {
    return `0${cleaned.slice(3)}`
  }
  return cleaned
}

const getOtpCooldownStorageKey = (tenantId: string) => `huay-register-otp-cooldown:${tenantId || 'unknown'}`
const getOtpFlowStorageKey = (tenantId: string) => `huay-register-otp-flow:${tenantId || 'unknown'}`

const readPersistedOtpCooldown = (tenantId: string): PersistedOtpCooldown | null => {
  try {
    const raw = localStorage.getItem(getOtpCooldownStorageKey(tenantId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedOtpCooldown
    if (!parsed?.cooldownUntil || parsed.cooldownUntil <= Date.now()) {
      localStorage.removeItem(getOtpCooldownStorageKey(tenantId))
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const persistOtpCooldown = (tenantId: string, data: PersistedOtpCooldown) => {
  try {
    localStorage.setItem(getOtpCooldownStorageKey(tenantId), JSON.stringify(data))
  } catch {
    // Ignore storage failures.
  }
}

const clearPersistedOtpCooldown = (tenantId: string) => {
  try {
    localStorage.removeItem(getOtpCooldownStorageKey(tenantId))
  } catch {
    // Ignore storage cleanup failures.
  }
}

const readPersistedOtpFlow = (tenantId: string): PersistedOtpFlow | null => {
  try {
    const raw = localStorage.getItem(getOtpFlowStorageKey(tenantId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedOtpFlow
    if (!parsed?.phone || !parsed?.sessionId || !parsed?.otpRequested) {
      return null
    }
    if (!parsed.cooldownUntil || parsed.cooldownUntil <= Date.now()) {
      localStorage.removeItem(getOtpFlowStorageKey(tenantId))
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const persistOtpFlow = (tenantId: string, data: PersistedOtpFlow) => {
  try {
    localStorage.setItem(getOtpFlowStorageKey(tenantId), JSON.stringify(data))
  } catch {
    // Ignore storage failures.
  }
}

const clearPersistedOtpFlow = (tenantId: string) => {
  try {
    localStorage.removeItem(getOtpFlowStorageKey(tenantId))
  } catch {
    // Ignore storage cleanup failures.
  }
}

const ModernRegisterPage: React.FC<ModernRegisterPageProps> = ({ themeConfig }) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const lineBridgeHandledRef = useRef(false)
  const [phone, setPhone] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpRequested, setOtpRequested] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [loadingOtp, setLoadingOtp] = useState(false)
  const [loadingVerify, setLoadingVerify] = useState(false)
  const [tryFirebaseClient, setTryFirebaseClient] = useState(false)
  const [preferredBackendProvider, setPreferredBackendProvider] = useState<string | null>(null)
  const [smsConfigReady, setSmsConfigReady] = useState(false)
  /** เวลาที่กดขอ OTP ได้อีก (timestamp) — หลังกดขอ OTP แล้วต้องรอจนถึงเวลานี้ */
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null)
  const [, setTick] = useState(0)
  /** เหมือน user-classic: ข้อความใต้ช่องเบอร์ (เบอร์ซ้ำ / พร้อมใช้งาน / กำลังตรวจสอบ) */
  const [phoneValidationMessage, setPhoneValidationMessage] = useState('')
  const [phoneExistsInSystem, setPhoneExistsInSystem] = useState(false)
  const [isCheckingPhone, setIsCheckingPhone] = useState(false)
  const [phoneCanUseOtp, setPhoneCanUseOtp] = useState(false)

  useEffect(() => {
    const ref = searchParams.get('ref')
    const aff = searchParams.get('aff')
    if (ref && ref.trim()) {
      resolveReferralToken(ref.trim())
        .then(({ referralCode }) => {
          sessionStorage.setItem(AFFILIATE_REFERRAL_KEY, referralCode)
        })
        .catch(() => { /* invalid/expired ref */ })
    } else if (aff && aff.trim()) {
      sessionStorage.setItem(AFFILIATE_REFERRAL_KEY, aff.trim())
    }
  }, [searchParams])

  useEffect(() => {
    if (lineBridgeHandledRef.current || !themeConfig?.tenantId) {
      return
    }

    const lineBridge = searchParams.get('lineBridge')
    if (!lineBridge) {
      return
    }

    lineBridgeHandledRef.current = true
    const tenantId = themeConfig.tenantId
    const lineFlow = loadLineBridgeFlowContext()
    const apiBaseUrl = getApiBaseUrl()

    const clearLineBridgeQuery = () => {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('lineBridge')
      nextParams.delete('flowId')
      nextParams.delete('firebaseUid')
      nextParams.delete('sessionId')
      nextParams.delete('errorMessage')
      setSearchParams(nextParams, { replace: true })
    }

    if (lineBridge === 'sent' && lineFlow) {
      setPhone(toThaiPhoneDisplay(lineFlow.phoneNumber))
      setSessionId(lineFlow.sessionId)
      setOtpRequested(true)
      setOtpCode('')
      setCooldownUntil(Date.now() + 5 * 60 * 1000)
      recordRegistrationOTPSent(tenantId, toThaiPhoneDisplay(lineFlow.phoneNumber)).catch(() => {})
      reportFirebaseSmsBilling({
        apiBaseUrl: lineFlow.apiBaseUrl || apiBaseUrl,
        tenantId: lineFlow.tenantId || tenantId,
        phoneNumber: lineFlow.phoneNumber,
        status: 'sent',
        messageType: 'registration',
        source: 'frontend-modern-registration-bridge-line',
      })
      clearLineBridgeQuery()
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 3000,
        icon: 'success',
        title: 'ส่ง OTP ไปยังเบอร์โทรศัพท์แล้ว กรุณากรอกรหัส 6 หลัก',
      })
      return
    }

    if (lineBridge === 'verified') {
      const sessionIdFromQuery = searchParams.get('sessionId') || lineFlow?.sessionId || sessionId || ''
      const phoneFromFlow = normalizeThaiMobile(
        toThaiPhoneDisplay(lineFlow?.phoneNumber || '') || phone
      )

      clearLineBridgeFlowContext()
      clearLineBridgeQuery()

      if (!phoneFromFlow || !sessionIdFromQuery) {
        void Swal.fire({
          icon: 'error',
          title: 'ข้อมูลไม่ครบถ้วน',
          text: 'ไม่พบข้อมูลการยืนยัน OTP กรุณาเริ่มสมัครใหม่',
          confirmButtonText: 'ตกลง',
        }).then(() => {
          navigate('/register', { replace: true })
        })
        return
      }

      if (phoneFromFlow) setPhone(phoneFromFlow)
      if (sessionIdFromQuery) setSessionId(sessionIdFromQuery)

      void Swal.fire({
        title: 'ยืนยันเบอร์โทรสำเร็จ',
        text: 'กรุณากรอกรหัสผ่านและข้อมูลบัญชีในหน้าถัดไป',
        icon: 'success',
        timer: 2500,
        showConfirmButton: false,
      }).then(() => {
        const completeState: ModernRegisterCompleteFlowState = {
          fromOtp: true,
          phone: phoneFromFlow,
          sessionId: sessionIdFromQuery,
        }
        persistModernRegisterCompleteFlowState(completeState)
        clearPersistedOtpFlow(tenantId)
        navigate('/register/complete', {
          replace: true,
          state: completeState,
        })
      })
      return
    }

    if (lineBridge === 'error') {
      clearLineBridgeFlowContext()
      clearLineBridgeQuery()
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 5000,
        icon: 'error',
        title: searchParams.get('errorMessage') || 'Firebase auth bridge ส่ง OTP ไม่สำเร็จ',
      })
    }
  }, [navigate, phone, searchParams, sessionId, setSearchParams, themeConfig?.tenantId])

  useEffect(() => {
    if (!themeConfig?.tenantId) return
    const persisted = readPersistedOtpCooldown(themeConfig.tenantId)
    if (!persisted) return

    setCooldownUntil(persisted.cooldownUntil)
    setPhone((prev) => prev || persisted.phoneNumber || '')
  }, [themeConfig?.tenantId])

  useEffect(() => {
    if (!themeConfig?.tenantId) return
    const persisted = readPersistedOtpFlow(themeConfig.tenantId)
    if (!persisted) return

    setPhone((prev) => prev || persisted.phone || '')
    setSessionId((prev) => prev || persisted.sessionId)
    setOtpRequested(persisted.otpRequested)
    const persistedCooldownUntil = persisted.cooldownUntil
    if (persistedCooldownUntil && persistedCooldownUntil > Date.now()) {
      setCooldownUntil((prev) => prev && prev > persistedCooldownUntil ? prev : persistedCooldownUntil)
    }
  }, [themeConfig?.tenantId])


  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/', { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    if (!themeConfig?.tenantId) return
    const tenantId = themeConfig.tenantId
    getSMSConfigPublic(tenantId)
      .then((data) => {
        setSmsConfigReady(true)
        const orderedProviders = (data?.providers || [])
          .filter((provider) => typeof provider?.type === 'string' && provider.type.trim())
          .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999))
        const smsEnabled = data?.smsEnabled === true
        const primary = orderedProviders[0]?.type ?? data?.primaryProvider ?? (smsEnabled ? 'firebase' : 'unknown')
        setPreferredBackendProvider(null)
        setTryFirebaseClient(smsEnabled && (primary === 'firebase' || orderedProviders.length === 0))
      })
      .catch(() => {
        setSmsConfigReady(true)
        setPreferredBackendProvider(null)
        setTryFirebaseClient(false)
      })
  }, [themeConfig?.tenantId])

  // เมื่อมีเบอร์ครบ 10 หลัก — เช็ค cooldown จาก backend (ดักที่ backend ไม่ให้ reload ลัด)
  useEffect(() => {
    if (!themeConfig?.tenantId || !phone) return
    const norm = normalizeThaiMobile(phone.trim())
    if (!isValidThaiMobile(norm)) {
      setPhoneCanUseOtp(false)
      return
    }
    checkRegistrationOTPCooldown(themeConfig.tenantId, norm).then((r) => {
      if (r.success && !r.allowed && r.remainingTimeMs && r.remainingTimeMs > 0) {
        setCooldownUntil(Date.now() + r.remainingTimeMs)
        setPhoneCanUseOtp(false)
        return
      }
      if (r.success && r.allowed) {
        setPhoneCanUseOtp(true)
      }
    }).catch(() => {
      setPhoneCanUseOtp(false)
    })
  }, [themeConfig?.tenantId, phone])

  useEffect(() => {
    if (!themeConfig?.tenantId) return

    if (cooldownUntil != null && cooldownUntil > Date.now()) {
      const existing = readPersistedOtpCooldown(themeConfig.tenantId)
      persistOtpCooldown(themeConfig.tenantId, {
        phoneNumber: phone || existing?.phoneNumber || '',
        cooldownUntil,
      })
      return
    }

    clearPersistedOtpCooldown(themeConfig.tenantId)
  }, [themeConfig?.tenantId, phone, cooldownUntil])

  useEffect(() => {
    if (!themeConfig?.tenantId) return

    if (otpRequested && sessionId && cooldownUntil && cooldownUntil > Date.now()) {
      persistOtpFlow(themeConfig.tenantId, {
        phone,
        otpRequested,
        sessionId,
        cooldownUntil,
      })
      return
    }

    clearPersistedOtpFlow(themeConfig.tenantId)
  }, [themeConfig?.tenantId, phone, otpRequested, sessionId, cooldownUntil])

  // 🔍 Real-time เช็คเบอร์มีในระบบหรือไม่เมื่อพิมพ์ครบ 10 หลัก (เหมือน user-classic) — แจ้ง inline + SweetAlert แล้ว redirect
  useEffect(() => {
    const tid = themeConfig?.tenantId
    if (!tid) return
    const trimmed = phone.trim().replace(/\D/g, '')
    if (trimmed.length !== 10) {
      setPhoneValidationMessage('')
      setPhoneExistsInSystem(false)
      setPhoneCanUseOtp(false)
      return
    }
    const norm = normalizeThaiMobile(trimmed)
    if (!isValidThaiMobile(norm)) {
      setPhoneValidationMessage('')
      setPhoneExistsInSystem(false)
      setPhoneCanUseOtp(false)
      return
    }
    let cancelled = false
    setIsCheckingPhone(true)
    setPhoneValidationMessage('กำลังตรวจสอบเบอร์มือถือ...')
    checkMobileAvailability(tid, norm)
      .then((res) => {
        if (cancelled) return
        if (res.success) {
          if (res.available) {
            setPhoneExistsInSystem(false)
            setPhoneValidationMessage('เบอร์นี้สามารถใช้งานได้')
          } else {
            setPhoneExistsInSystem(true)
            setPhoneCanUseOtp(false)
            setPhoneValidationMessage('เบอร์มือถือนี้มีในระบบแล้ว')
            showPhoneExistsPopup(norm)
          }
        } else {
          setPhoneExistsInSystem(false)
          setPhoneCanUseOtp(false)
          setPhoneValidationMessage('ไม่สามารถตรวจสอบเบอร์มือถือได้')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPhoneExistsInSystem(false)
          setPhoneCanUseOtp(false)
          setPhoneValidationMessage('เกิดข้อผิดพลาดในการตรวจสอบ')
        }
      })
      .finally(() => {
        if (!cancelled) setIsCheckingPhone(false)
      })
    return () => {
      cancelled = true
    }
  }, [themeConfig?.tenantId, phone])

  // อัปเดต countdown ทุก 1 วินาที เมื่ออยู่ระหว่าง cooldown
  useEffect(() => {
    if (cooldownUntil == null) return
    const remaining = cooldownUntil - Date.now()
    if (remaining <= 0) {
      setCooldownUntil(null)
      return
    }
    const t = setInterval(() => {
      setTick((n) => n + 1)
      if (Date.now() >= cooldownUntil) setCooldownUntil(null)
    }, 1000)
    return () => clearInterval(t)
  }, [cooldownUntil])

  useEffect(() => {
    document.body.classList.add('htd-auth-active')
    return () => document.body.classList.remove('htd-auth-active')
  }, [])

  const bgUrl = useMemo(() => {
    if (!themeConfig) return ''
    const folder = getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue'
    const cdn =
      (import.meta.env as { VITE_CDN_URL?: string }).VITE_CDN_URL ||
      'https://huay-cdn-assets-prod.s3.ap-southeast-1.amazonaws.com'
    return `${cdn}/${folder}/img/bg.jpg`
  }, [themeConfig])

  if (!themeConfig) {
    return (
      <div className="htd-auth htd-auth--loading">
        <p>กำลังโหลด...</p>
      </div>
    )
  }

  const tenantId = themeConfig.tenantId || getRuntimeTenantIdSync()
  const logoUrl = themeConfig.theme?.branding?.logoUrl
  const siteName = themeConfig.theme?.branding?.siteName || 'หวยทูเดย์'
  /** ใช้ public SMS config เป็นตัวตัดสินหลัก เพื่อให้ bridge-only ทำงานได้แม้ tenant theme ยังไม่ sync */
  const smsOtpEnabled = smsConfigReady
    ? (tryFirebaseClient || !!preferredBackendProvider)
    : themeConfig.settings?.smsOtpEnabled === true

  /** เบอร์มีในระบบแล้ว: แจ้งเตือน + นับถอยหลัง 15 วินาที (ห้ามปิด) แล้ว redirect /login */
  const showPhoneExistsPopup = async (phoneDisplay: string) => {
    await Swal.fire({
      icon: 'warning',
      title: 'มีเบอร์นี้ในระบบแล้ว',
      html: `
        <p>เบอร์ <strong>${phoneDisplay}</strong> มีในระบบอยู่แล้ว</p>
        <p class="mb-0">หากไม่ใช่ข้อมูลคุณ กรุณาติดต่อแอดมิน</p>
        <p class="mt-2 mb-0">จะนำคุณไปยังหน้าเข้าสู่ระบบใน <span id="countdown">15</span> วินาที</p>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      timer: 15000,
      timerProgressBar: true,
      didOpen: () => {
        const el = document.getElementById('countdown')
        let n = 15
        const iv = setInterval(() => {
          n--
          if (el) el.textContent = String(n)
          if (n <= 0) clearInterval(iv)
        }, 1000)
      },
    })
    navigate('/login', { replace: true })
  }

  const handleRequestOtp = async () => {
    const trimmed = phone.trim()
    if (!trimmed) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'กรุณาใส่เบอร์โทรศัพท์' })
      return
    }
    if (!isValidThaiMobile(normalizeThaiMobile(trimmed))) {
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 3500,
        icon: 'warning',
        title: 'เบอร์โทรศัพท์ไม่ถูกต้อง (ใช้ได้เฉพาะ 10 หลัก ขึ้นต้น 06, 08, 09)',
      })
      return
    }
    setLoadingOtp(true)
    try {
      const bridgeEnabled = tryFirebaseClient && shouldUseFirebaseAuthBridge()
      const lineBridgeRedirect = bridgeEnabled && shouldUseFirebaseAuthBridgeRedirect()
      let bridgePrimed = false

      if (bridgeEnabled && !lineBridgeRedirect) {
        try {
          primeFirebaseAuthBridgeWindow()
          bridgePrimed = true
        } catch (primeError) {
          console.warn('⚠️ Failed to pre-open Firebase auth bridge popup:', primeError)
        }
      }

      const cooldownCheck = await checkRegistrationOTPCooldown(tenantId, trimmed)
      if (cooldownCheck.success && !cooldownCheck.allowed && (cooldownCheck.remainingTimeMs ?? 0) > 0) {
        setCooldownUntil(Date.now() + (cooldownCheck.remainingTimeMs ?? 0))
        Swal.fire({
          toast: true,
          position: 'top',
          timer: 5000,
          icon: 'warning',
          title: `กรุณารออีก ${cooldownCheck.remainingTimeMin ?? 0} นาที จึงสามารถขอ OTP ใหม่ได้`,
        })
        if (bridgePrimed) closeFirebaseAuthBridge()
        setLoadingOtp(false)
        return
      }
      // 🔍 เช็คเบอร์มีในระบบหรือไม่ก่อนขอ OTP (เหมือน user-classic) — ถ้าซ้ำ แจ้ง SweetAlert แล้ว redirect /login
      const check = await checkMobileAvailability(tenantId, trimmed)
      if (!check.success) {
        setLoadingOtp(false)
        Swal.fire({
          toast: true,
          position: 'top',
          timer: 4000,
          icon: 'warning',
          title: check.error || 'ไม่สามารถตรวจสอบเบอร์มือถือได้',
        })
        if (bridgePrimed) closeFirebaseAuthBridge()
        return
      }
      if (!check.available) {
        if (bridgePrimed) closeFirebaseAuthBridge()
        setLoadingOtp(false)
        await showPhoneExistsPopup(normalizeThaiMobile(trimmed))
        return
      }
      if (!tryFirebaseClient) {
        if (bridgePrimed) closeFirebaseAuthBridge()
        throw new Error('ระบบ OTP ยังไม่พร้อม: tenant นี้ต้องใช้ Firebase ผ่าน central auth bridge เท่านั้น')
      }

      if (!bridgeEnabled) {
        if (bridgePrimed) closeFirebaseAuthBridge()
        throw new Error('Firebase auth bridge ยังไม่พร้อมสำหรับโดเมนนี้')
      }

      try {
        const e164Phone = toE164Thai(trimmed)
        const apiBaseUrl = getApiBaseUrl()

        if (lineBridgeRedirect) {
          const nextSessionId = `firebase_bridge_line_${Date.now()}`
          const flowId = crypto.randomUUID()
          saveLineBridgeFlowContext({
            flowId,
            tenantId,
            phoneNumber: e164Phone,
            apiBaseUrl,
            sessionId: nextSessionId,
            createdAt: Date.now(),
          })
          redirectToFirebaseAuthBridgeSend({
            tenantId,
            phoneNumber: e164Phone,
            apiBaseUrl,
            returnUrl: buildHashRouterReturnUrl('/register'),
          }, flowId)
          return
        }

        await sendOTPViaFirebaseAuthBridge({
          tenantId,
          phoneNumber: e164Phone,
          apiBaseUrl,
        })
        const sid = 'firebase_bridge_' + Date.now()
        setSessionId(sid)
        setOtpRequested(true)
        setOtpCode('')
        reportFirebaseSMSUsage(tenantId, e164Phone)
        recordRegistrationOTPSent(tenantId, trimmed).catch(() => {})
        const waitMs = 5 * 60 * 1000
        setCooldownUntil(Date.now() + waitMs)
        Swal.fire({
          toast: true,
          position: 'top',
          timer: 3000,
          icon: 'success',
          title: 'ส่ง OTP ไปยังเบอร์โทรศัพท์แล้ว กรุณากรอกรหัส 6 หลัก',
        })
        setLoadingOtp(false)
        return
      } catch (bridgeError) {
        if (bridgePrimed) closeFirebaseAuthBridge()
        throw new Error(
          bridgeError instanceof Error
            ? bridgeError.message
            : 'Firebase auth bridge ส่ง OTP ไม่สำเร็จ'
        )
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string }
      const msg = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่'
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 4000,
        icon: 'error',
        title: msg,
      })
    } finally {
      setLoadingOtp(false)
    }
  }

  /** เมื่อปิด OTP: กด "ถัดไป" → เช็คเบอร์มีในระบบหรือไม่ ถ้ามี แจ้งเตือน 15 วินาที แล้วไป /login */
  const handleNextWithoutOtp = async () => {
    const trimmed = phone.trim()
    if (!trimmed) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'กรุณาใส่เบอร์โทรศัพท์' })
      return
    }
    if (!isValidThaiMobile(normalizeThaiMobile(trimmed))) {
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 3500,
        icon: 'warning',
        title: 'เบอร์โทรศัพท์ไม่ถูกต้อง (ใช้ได้เฉพาะ 10 หลัก ขึ้นต้น 06, 08, 09)',
      })
      return
    }
    const check = await checkMobileAvailability(tenantId, trimmed)
    if (check.success && !check.available) {
      await showPhoneExistsPopup(normalizeThaiMobile(trimmed))
      return
    }
    navigate('/login', { replace: true })
  }

  const handleVerifyOtp = async () => {
    const trimmed = phone.trim()
    if (!trimmed) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'กรุณาใส่เบอร์โทรศัพท์และขอ OTP ก่อน' })
      return
    }
    if (!otpRequested || !sessionId) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'กรุณากด ขอ OTP ก่อน' })
      return
    }
    if (!/^\d{6}$/.test(otpCode)) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'กรุณากรอกรหัส OTP 6 หลัก' })
      return
    }
    setLoadingVerify(true)
    try {
      if (sessionId.startsWith('firebase_bridge_line_')) {
        const lineFlow = loadLineBridgeFlowContext()
        if (!lineFlow) {
          throw new Error('ไม่พบ session สำหรับยืนยัน OTP กรุณาเริ่มสมัครใหม่')
        }

        redirectToFirebaseAuthBridgeVerify({
          flowId: lineFlow.flowId,
          otpCode,
          returnUrl: buildHashRouterReturnUrl('/register'),
        })
        return
      }

      if (sessionId.startsWith('firebase_bridge_')) {
        await verifyOTPViaFirebaseAuthBridge(otpCode)
        closeFirebaseAuthBridge()
        Swal.fire({
          title: 'ยืนยันเบอร์โทรสำเร็จ',
          text: 'กรุณากรอกรหัสผ่านและข้อมูลบัญชีในหน้าถัดไป',
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
        }).then(() => goToComplete())
      } else if (sessionId.startsWith('firebase_') && window.phoneConfirmation) {
        await window.phoneConfirmation.confirm(otpCode)
        window.phoneConfirmation = undefined
        Swal.fire({
          title: 'ยืนยันเบอร์โทรสำเร็จ',
          text: 'กรุณากรอกรหัสผ่านและข้อมูลบัญชีในหน้าถัดไป',
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
        }).then(() => goToComplete())
      } else {
        const result = await verifyRegistrationOTP(tenantId, trimmed, otpCode, sessionId)
        if (result.success) {
          Swal.fire({
            title: 'ยืนยันเบอร์โทรสำเร็จ',
            text: 'กรุณากรอกรหัสผ่านและข้อมูลบัญชีในหน้าถัดไป',
            icon: 'success',
            timer: 2500,
            showConfirmButton: false,
          }).then(() => goToComplete())
        } else {
          Swal.fire({
            toast: true,
            position: 'top',
            timer: 4000,
            icon: 'error',
            title: result.message || 'ยืนยัน OTP ไม่สำเร็จ',
          })
        }
      }
    } catch (e) {
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 3000,
        icon: 'error',
        title: 'เกิดข้อผิดพลาด กรุณาลองใหม่',
      })
    } finally {
      setLoadingVerify(false)
    }
  }

  const goToComplete = () => {
    const trimmed = phone.trim()
    if (!trimmed || !sessionId) return

    const completeState: ModernRegisterCompleteFlowState = {
      fromOtp: true,
      phone: normalizeThaiMobile(trimmed),
      sessionId,
    }
    persistModernRegisterCompleteFlowState(completeState)
    clearPersistedOtpFlow(tenantId)
    navigate('/register/complete', {
      replace: true,
      state: completeState,
    })
  }

  const remainingMs = cooldownUntil != null ? Math.max(0, cooldownUntil - Date.now()) : 0
  const inCooldown = remainingMs > 0

  return (
    <div
      className="htd-auth"
      style={bgUrl ? ({ ['--htd-auth-bg' as string]: `url(${bgUrl})` } as React.CSSProperties) : undefined}
    >
      <div className="htd-auth__ambient" aria-hidden>
        <span className="htd-auth__orb htd-auth__orb--a" />
        <span className="htd-auth__orb htd-auth__orb--b" />
      </div>

      <div className="htd-auth__shell">
        <Link to="/" className="htd-auth__brand" aria-label={siteName}>
          {logoUrl ? <img src={logoUrl} alt={siteName} /> : <span>{siteName}</span>}
        </Link>

        <div className="htd-auth__card">
          <div className="htd-auth__head">
            <span className="htd-auth__step">STEP 1</span>
            <h1 className="htd-auth__title">สมัครสมาชิก</h1>
            <p className="htd-auth__sub">
              {smsOtpEnabled ? 'กรอกเบอร์โทรศัพท์และยืนยัน OTP เพื่อเริ่มสมัคร' : 'กรอกเบอร์โทรศัพท์เพื่อเริ่มสมัครสมาชิก'}
            </p>
          </div>

          {themeConfig.settings?.lineLoginEnabled !== false && (
            <>
              <button
                type="button"
                className="htd-auth__line"
                onClick={() => {
                  void Swal.fire({
                    icon: 'info',
                    title: 'เร็ว ๆ นี้',
                    text: 'ระบบสมัครด้วย LINE กำลังเตรียมเปิดให้บริการ',
                    confirmButtonColor: 'var(--htd-primary)',
                  })
                }}
              >
                <i className="fab fa-line" aria-hidden />
                ลงชื่อเข้าใช้ด้วย LINE
              </button>
              <div className="htd-auth__divider">หรือ</div>
            </>
          )}

          <label className="htd-auth__field" htmlFor="register-phone">
            <span>เบอร์โทรศัพท์</span>
            <div
              className={`htd-auth__input-wrap${
                phoneExistsInSystem ? ' htd-auth__input-wrap--error' : ''
              }${!smsOtpEnabled && phoneCanUseOtp && !phoneExistsInSystem && !isCheckingPhone ? ' htd-auth__input-wrap--pad' : ''}`}
            >
              <i className="fas fa-mobile-alt htd-auth__lead" aria-hidden />
              <input
                id="register-phone"
                type="tel"
                inputMode="numeric"
                placeholder="เบอร์โทรศัพท์ (06, 08, 09)"
                value={phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                  setPhone(value)
                }}
                disabled={loadingOtp}
                maxLength={10}
              />
              {phoneCanUseOtp && !phoneExistsInSystem && !isCheckingPhone && !smsOtpEnabled && (
                <i className="fas fa-check-circle htd-auth__state" style={{ color: '#16a34a' }} aria-hidden />
              )}
              {phoneCanUseOtp && !phoneExistsInSystem && !isCheckingPhone && smsOtpEnabled && (
                <i className="fas fa-check-circle htd-auth__ok" aria-hidden />
              )}
              {smsOtpEnabled && (
                <button
                  type="button"
                  className="htd-auth__otp"
                  onClick={handleRequestOtp}
                  disabled={loadingOtp || inCooldown}
                >
                  {loadingOtp ? 'กำลังส่ง...' : inCooldown ? 'ขอใหม่' : 'ขอ OTP'}
                </button>
              )}
            </div>
          </label>

          {phoneValidationMessage && (
            <p
              className={`htd-auth__hint ${
                phoneExistsInSystem ? 'htd-auth__hint--error' : isCheckingPhone ? '' : 'htd-auth__hint--ok'
              }`}
            >
              {phoneExistsInSystem && '❌ '}
              {phoneValidationMessage}
            </p>
          )}

          {smsOtpEnabled && inCooldown && (
            <>
              <p className="htd-auth__hint htd-auth__hint--warn">
                {(() => {
                  const min = Math.floor(remainingMs / 60000)
                  const sec = Math.floor((remainingMs % 60000) / 1000)
                  return min > 0
                    ? `กรุณารออีก ${min} นาที ${sec} วินาที จึงสามารถขอ OTP ใหม่ได้`
                    : `กรุณารออีก ${sec} วินาที จึงสามารถขอ OTP ใหม่ได้`
                })()}
              </p>
              {otpRequested ? (
                <p className="htd-auth__hint htd-auth__hint--info">คุณยังสามารถใช้ OTP เดิมที่ได้รับเพื่อยืนยันต่อด้านล่างได้</p>
              ) : null}
            </>
          )}

          {!smsOtpEnabled && (
            <p className="htd-auth__hint htd-auth__hint--ok">✔ ไม่ต้องยืนยัน SMS สำหรับการสมัครสมาชิก</p>
          )}

          {smsOtpEnabled && otpRequested && (
            <label className="htd-auth__field" htmlFor="register-otp">
              <span>รหัส OTP</span>
              <div className="htd-auth__input-wrap">
                <i className="fas fa-key htd-auth__lead" aria-hidden />
                <input
                  id="register-otp"
                  type="text"
                  inputMode="numeric"
                  placeholder="รหัส OTP 6 หลัก"
                  value={otpCode}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setOtpCode(v)
                  }}
                  disabled={loadingVerify}
                  maxLength={6}
                />
              </div>
            </label>
          )}

          {smsOtpEnabled ? (
            <button
              type="button"
              className="htd-auth__submit"
              onClick={handleVerifyOtp}
              disabled={loadingVerify || !otpRequested}
            >
              {loadingVerify ? 'กำลังยืนยัน...' : 'ยืนยัน OTP'}
            </button>
          ) : (
            <button type="button" className="htd-auth__submit" onClick={handleNextWithoutOtp}>
              ถัดไป
            </button>
          )}

          <p className="htd-auth__foot">
            มีบัญชีอยู่แล้ว? <Link to="/login">เข้าสู่ระบบ</Link>
          </p>

          <Link to="/" className="htd-auth__back">
            <i className="fas fa-chevron-left" aria-hidden />
            กลับหน้าแรก
          </Link>
        </div>
      </div>
    </div>
  )
}

export default ModernRegisterPage
